import { isTagReadable } from "@/components/ui/tags/tag-accessibility";
import { type OnlyProjectFilteringStore } from "@/store/project-filtering/project-filtering-slice";
import { GUID } from "@faro-lotv/foundation";
import { IElement } from "@faro-lotv/ielement-types";
import { OnlyIElementsStore } from "../ielements-slice-selector";
import { Tag, TagsState, UNTAGGED } from "./tags-slice";

export type OnlyTagsStore = { tags: TagsState };

type ElementsFilteringState = OnlyIElementsStore & OnlyProjectFilteringStore;

/**
 * @returns All the tags in the project
 */
export function selectTags({ tags }: OnlyTagsStore): Tag[] {
  return tags.tags;
}

/**
 * @returns All the system tags in the project
 */
export function selectSystemTags({ tags }: OnlyTagsStore): Tag[] {
  return tags.systemTags;
}

/**
 *
 * @param elements list of elements to filter
 * @param selectReferenceElement function used to find an element of a specific type.
 * If defined it means that the tags are contained in an ancestor or child of the passed elements
 * @returns a filtered version of the passed list of element. It's filtered based on if any of the tags of the element is also present in
 * the list of selected tags by the user
 */
export function selectFilteredElementsWithTags<
  T extends IElement,
  K extends IElement,
>(
  elements: T[],
  selectReferenceElement?: (
    element: T,
  ) => (store: OnlyIElementsStore) => K | undefined,
): (state: ElementsFilteringState) => T[] {
  return (state: ElementsFilteringState): T[] => {
    // If the predicate is defined then get the tagged IElement, otherwise use directly the passed list
    const elementsToFilter = selectReferenceElement
      ? elements.flatMap((el) => selectReferenceElement(el)(state))
      : elements;

    // Get the selected tags from the project filtering store
    const selectedTagsIds = state.projectFiltering.tags ?? [];

    // Filtered list of the passed elements
    let filteredElements: T[] = [];

    // If the store does not have any selected tags, then return the passed list of elements
    if (selectedTagsIds.length === 0) {
      filteredElements = elements;
    } else {
      for (const [idx, el] of elementsToFilter.entries()) {
        if (matchIElementTag(el, selectedTagsIds)) {
          filteredElements.push(elements[idx]);
        }
      }
    }

    return filteredElements;
  };
}

/**
 * @returns True if the input element labels match one of the provided tags
 * @param iElement the iElement to check
 * @param tags The list of tags used to check for matching
 */
function matchIElementTag(
  iElement: IElement | undefined,
  tags: GUID[],
): boolean {
  if (!iElement) {
    return false;
  }

  // If the current element does not have tags (it's untagged) and the selected tags in the store
  // have the UNTAGGED tag, then keep the element so that it's returned.
  // Otherwise, if any tag present in the list of tags of the current element is also present in the list of
  // selected tags in the store, then keep the element so that it's returned.
  if (
    (isIElementUntagged(iElement) && tags.includes(UNTAGGED.id)) ||
    hasIElementTag(iElement, tags)
  ) {
    return true;
  }

  return false;
}

/**
 * @returns True if the iElement contains at least one of the tags in the input list
 * @param iElement The iElement to check
 * @param tags The list of tags used to check against
 */
function hasIElementTag(iElement: IElement, tags: GUID[]): boolean {
  // Get the ids of the tags of the current element in the list
  const labelsIds = iElement.labels
    ?.filter(isTagReadable)
    .map((label) => label.id);
  return !!labelsIds?.some((labelId) => tags.includes(labelId));
}

/**
 * @returns True if the iElement has no readable labels
 * @param iElement The iElement to check
 */
function isIElementUntagged(iElement: IElement): boolean {
  return !iElement.labels || iElement.labels.filter(isTagReadable).length === 0;
}

/**
 *
 * @param state The application store state
 * @returns The store's tags slice
 */
export function selectTagsSlice({ tags }: OnlyTagsStore): TagsState {
  return tags;
}
